import React from 'react';

import { AccordionItem } from '@radix-ui/react-accordion';
import { useTranslation } from 'react-i18next';

import { ChipSize } from '@pkg/components/Sections/Profile/Chip';
import { ChipMember } from '@pkg/components/Sections/Profile/Chips/ChipMember';
import { FilterAction, FilterContent, FilterHeading, type FilterIndex } from '@pkg/components/Sections/Profile/Filters';
import { getPagesContent, useGetGroupMembers, useGroupId } from '@pkg/hooks';
import { NftFiltersKey, type NftMembersSingle, useNftFilter } from '@pkg/providers';

import { useAuthTokens } from '@app/store/zustand/Auth/useUserTokens';

const FILTER_KEY = NftFiltersKey.Members;

export function GroupFilterMembers({ value }: FilterIndex) {
    const { t: translate } = useTranslation(['groups']);
    const isAuthenticated = useAuthTokens((state) => state.isAuthenticated);
    // TODO check if we need this via props
    const groupId = useGroupId();
    const { data, isError, isLoading } = useGetGroupMembers({ isAuthenticated, groupId });

    const [filters, setFilters] = useNftFilter();

    function handleUpdateMembers(member: NftMembersSingle) {
        if (filters.hasSomeValue(FILTER_KEY, member)) {
            filters.remove(FILTER_KEY, member);
        } else {
            filters.append(FILTER_KEY, member);
        }

        setFilters(filters);
    }

    const members = getPagesContent(data?.pages);
    const totalMembers = data?.pages?.[0]?.paging.total || 0;

    if (isError || isLoading) {
        /**
         * On an error from the api we don't show the filter.
         * Might need to handle if the members array is empty too.
         */
        return null;
    }

    const membersItemsMarkup = members.length
        ? members.map(({ id, primary_yat, twitter }) => {
              if (!primary_yat) return;

              return (
                  <FilterAction
                      key={`GroupFilterMembers-${id}`}
                      pressed={filters.hasSomeValue(FILTER_KEY, id)}
                      onAction={() => handleUpdateMembers(id)}
                  >
                      <ChipMember yat={primary_yat} twitter={twitter} size={ChipSize.Smaller} />
                  </FilterAction>
              );
          })
        : null;

    return (
        <AccordionItem value={value}>
            <FilterHeading disableUpperCase>
                {totalMembers > 1
                    ? translate('groups_filter_members_title', { count: totalMembers })
                    : translate('groups_filter_members_title_single')}
            </FilterHeading>

            <FilterContent height="165px" containScroll>
                {membersItemsMarkup}
            </FilterContent>
        </AccordionItem>
    );
}
